import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FiChevronDown, FiChevronUp, FiPackage } from "react-icons/fi";
import ImagePickerInput from "../ImagePickerInput";
import TaxonomyMultiSelect from "../TaxonomyMultiSelect";
import type { AdminPage } from "./types";
import type { IApplicationState } from "../../Redux/Reducers";

type PageEditorMetadataPanelProps = {
	editable: boolean;
	draft: AdminPage | null;
	metaOpen: boolean;
	onToggleMeta: () => void;
	setDraft: React.Dispatch<React.SetStateAction<AdminPage | null>>;
	resolvedPath: string;
	keywordsToInput: (keywords?: string[] | null) => string;
	inputToKeywords: (value: string) => string[];
	canChangeSlug?: boolean;
	onOpenSlugChange?: () => void;
};

const buildTaxonomyLabel = (id: string, map: Map<string, string>) => map.get(id) ?? id;
const normalizeVariableKey = (value: string) =>
	value.trim().replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "");

/** Page metadata panel (slug, language, SEO, taxonomies, dynamic content). */
const PageEditorMetadataPanel: React.FC<PageEditorMetadataPanelProps> = ({
	editable,
	draft,
	metaOpen,
	onToggleMeta,
	setDraft,
	resolvedPath,
	keywordsToInput,
	inputToKeywords,
	canChangeSlug,
	onOpenSlugChange
}) => {
	const [copiedToken, setCopiedToken] = useState<string | null>(null);
	const [taxonomyEditing, setTaxonomyEditing] = useState(false);
	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);
	const contentTypeConfigs = useSelector((state: IApplicationState) => state.contentTypeConfigs.items);
	const taxonomyMap = useMemo(() => {
		const map = new Map<string, string>();
		taxonomies.forEach((taxonomie) => {
			if (taxonomie.id) {
				map.set(taxonomie.id, taxonomie.title ?? taxonomie.id);
			}
		});
		return map;
	}, [taxonomies]);
	const taxonomyIds = useMemo(
		() => (draft?.taxonomyIds ?? []).filter(Boolean),
		[draft?.taxonomyIds]
	);
	const dynamicTypeOptions = useMemo(
		() => (contentTypeConfigs ?? []).filter((config) => config.supportsDynamicPath),
		[contentTypeConfigs]
	);
	const selectedDynamicType = useMemo(
		() => dynamicTypeOptions.find((config) => config.id === draft?.dynamicContentTypeId) ?? null,
		[dynamicTypeOptions, draft?.dynamicContentTypeId]
	);
	const dynamicEnabled = !!draft?.dynamicContentTypeId;
	const handleCopyToken = async (token: string) => {
		try {
			if (navigator?.clipboard?.writeText) {
				await navigator.clipboard.writeText(token);
			} else {
				const textarea = document.createElement("textarea");
				textarea.value = token;
				textarea.style.position = "fixed";
				textarea.style.opacity = "0";
				document.body.appendChild(textarea);
				textarea.select();
				document.execCommand("copy");
				document.body.removeChild(textarea);
			}
			setCopiedToken(token);
			setTimeout(() => {
				setCopiedToken((current) => (current === token ? null : current));
			}, 1500);
		} catch {
			// ignore clipboard failures
		}
	};
	const variableTokens = useMemo(() => {
		if (!dynamicEnabled || !selectedDynamicType) return [];
		const base = ["path", "id", "slug", "taxonomies"];
		if (selectedDynamicType.supportsCategory) {
			base.push("categories", "categoryPath", "categoryTitle");
		}
		const fieldKeys = (selectedDynamicType.fields ?? [])
			.map((field) => field.key ?? normalizeVariableKey(field.title ?? ""))
			.map((value) => normalizeVariableKey(value))
			.filter(Boolean);
		const unique = new Set<string>();
		base.forEach((key) => unique.add(key));
		fieldKeys.forEach((key) => unique.add(key));
		return Array.from(unique).map((key) => `{${key}}`);
	}, [dynamicEnabled, selectedDynamicType]);

	return (
		<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
			<div className="flex items-center justify-between gap-3">
				<div>
					<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Metadata & routing</p>
					<p className="text-sm text-slate-600">Slug, language, SEO title and description.</p>
				</div>
				<div className="flex items-center gap-2">
					{editable && (
						<button
							type="button"
							className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50"
							onClick={() => {
								setDraft((prev) => {
									if (!prev) return prev;
									const metadataTitle = prev.metadata?.title || prev.title || "";
									return { ...prev, metadata: { ...(prev.metadata || {}), title: metadataTitle } };
								});
							}}
						>
							Sync SEO title
						</button>
					)}
					<button
						type="button"
						onClick={onToggleMeta}
						className="inline-flex items-center rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
						aria-expanded={metaOpen}
					>
						{metaOpen ? <FiChevronUp /> : <FiChevronDown />}
					</button>
				</div>
			</div>
			{metaOpen && (
				<>
					{draft?.source && (
						<div className="flex items-center justify-between gap-3 rounded-lg border border-[#FFA401]/45 bg-[#FFF8EC] px-3 py-2">
							<div className="flex min-w-0 items-center gap-2">
								<FiPackage className="h-4 w-4 shrink-0 text-[#FFA401]" />
								<div className="min-w-0">
									<p className="text-xs font-semibold text-[#0F2849]">Migrated content</p>
									<p className="truncate text-[11px] text-slate-500">
										Imported by the backend (source: <span className="font-mono">{draft.source}</span>).
										This tag is backend-set and not editable — you can only remove it.
									</p>
								</div>
							</div>
							{editable && (
								<button
									type="button"
									onClick={() => setDraft((prev) => (prev ? { ...prev, source: null } : prev))}
									className="shrink-0 rounded-full border border-[#FFA401]/55 bg-white px-3 py-1 text-[11px] font-semibold text-[#0F2849] transition hover:bg-[#FFF1D6]"
									title="Remove the migrated marker — save the page to apply"
								>
									Remove migrated tag
								</button>
							)}
						</div>
					)}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						<label className="space-y-1">
							<div className="flex items-center justify-between gap-2">
								<span className="text-xs font-semibold text-slate-500">Slug</span>
								{canChangeSlug && onOpenSlugChange && (
									<button
										type="button"
										onClick={onOpenSlugChange}
										className="rounded-full border border-slate-200 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600 hover:border-slate-300 hover:bg-slate-50"
									>
										Change slug
									</button>
								)}
							</div>
							<input
								disabled={!editable || !!draft?.id}
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
								placeholder="e.g. publications/weekly-update"
								value={draft?.slug || ""}
								onChange={(e) =>
									setDraft((prev) => (prev ? { ...prev, slug: e.target.value.trim() } : prev))
								}
							/>
						</label>
						<label className="space-y-1">
							<span className="text-xs font-semibold text-slate-500">Language</span>
							<input
								disabled={!editable}
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
								placeholder="fr"
								value={draft?.language || ""}
								onChange={(e) =>
									setDraft((prev) =>
										prev ? { ...prev, language: e.target.value.trim().toLowerCase() } : prev
									)
								}
							/>
						</label>
						<div className="space-y-2 sm:col-span-2">
							<div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
								<div>
									<p className="text-xs font-semibold text-slate-600">Dynamic page</p>
									<p className="text-xs text-slate-500">
										Bind this page to a dynamic content type (requires dynamic path).
									</p>
								</div>
								<label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
									<input
										type="checkbox"
										className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
										disabled={!editable}
										checked={dynamicEnabled}
										onChange={(e) => {
											const nextEnabled = e.target.checked;
											setDraft((prev) => {
												if (!prev) return prev;
												if (!nextEnabled) {
													return {
														...prev,
														dynamicContentTypeId: null,
														dynamicPreviewSlugs: [],
														dynamicProductionSlugs: []
													};
												}
												const fallbackId = dynamicTypeOptions[0]?.id ?? null;
												return {
													...prev,
													dynamicContentTypeId: prev.dynamicContentTypeId ?? fallbackId
												};
											});
										}}
									/>
									Enabled
								</label>
							</div>
							{dynamicEnabled && (
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
									<label className="space-y-1 sm:col-span-2">
										<span className="text-xs font-semibold text-slate-500">Content type</span>
										<select
											disabled={!editable}
											className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
											value={draft?.dynamicContentTypeId ?? ""}
											onChange={(e) =>
												setDraft((prev) =>
													prev ? { ...prev, dynamicContentTypeId: e.target.value || null } : prev
												)
											}
										>
											<option value="">Select a dynamic content type</option>
											{dynamicTypeOptions.map((config) => (
												<option key={config.id ?? config.title} value={config.id ?? ""}>
													{config.title ?? "Untitled"}
												</option>
											))}
										</select>
									</label>
									<div className="space-y-2 sm:col-span-2">
										<p className="text-xs font-semibold text-slate-500">Available variables</p>
										<div className="flex flex-wrap gap-2">
											{variableTokens.length ? (
												variableTokens.map((token) => (
													<button
														key={token}
														type="button"
														className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-mono text-slate-600 hover:border-slate-300 hover:bg-slate-50"
														onClick={() => handleCopyToken(token)}
														title={copiedToken === token ? "Copied" : "Click to copy"}
													>
														{token}
														{copiedToken === token ? " ✓" : ""}
													</button>
												))
											) : (
												<p className="text-xs text-slate-500">Select a content type to see variables.</p>
											)}
										</div>
									</div>
								</div>
							)}
						</div>
						<label className="space-y-1">
							<span className="text-xs font-semibold text-slate-500">Title</span>
							<input
								disabled={!editable}
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
								value={draft?.title || ""}
								onChange={(e) => setDraft((prev) => (prev ? { ...prev, title: e.target.value } : prev))}
							/>
						</label>
						<label className="space-y-1">
							<span className="text-xs font-semibold text-slate-500">SEO title (optional)</span>
							<input
								disabled={!editable}
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
								value={draft?.metadata?.title || ""}
								onChange={(e) =>
									setDraft((prev) =>
										prev
											? { ...prev, metadata: { ...(prev.metadata || {}), title: e.target.value } }
											: prev
									)
								}
							/>
						</label>
						<label className="space-y-1 sm:col-span-2">
							<span className="text-xs font-semibold text-slate-500">Description</span>
							<textarea
								disabled={!editable}
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
								rows={3}
								value={draft?.metadata?.description || ""}
								onChange={(e) =>
									setDraft((prev) =>
										prev
											? {
													...prev,
													metadata: { ...(prev.metadata || {}), description: e.target.value }
												}
											: prev
									)
								}
							/>
						</label>
						<label className="space-y-1">
							<span className="text-xs font-semibold text-slate-500">Canonical URL</span>
							<input
								disabled={!editable}
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
								placeholder="https://www.apa.cd/page/..."
								value={draft?.metadata?.canonicalUrl || ""}
								onChange={(e) =>
									setDraft((prev) =>
										prev
											? { ...prev, metadata: { ...(prev.metadata || {}), canonicalUrl: e.target.value } }
											: prev
									)
								}
							/>
						</label>
						<div className="space-y-1">
							<ImagePickerInput
								label="Open Graph image"
								value={draft?.metadata?.image || ""}
								helper="Select an image from storage (CDN key only)."
								onChange={(next) =>
									setDraft((prev) =>
										prev ? { ...prev, metadata: { ...(prev.metadata || {}), image: next ?? "" } } : prev
									)
								}
								disabled={!editable}
							/>
						</div>
						<label className="space-y-1 sm:col-span-2">
							<span className="text-xs font-semibold text-slate-500">Keywords (comma separated)</span>
							<input
								disabled={!editable}
								className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
								placeholder="banque, taux, inflation"
								value={keywordsToInput(draft?.metadata?.keywords)}
								onChange={(e) =>
									setDraft((prev) =>
										prev
											? {
													...prev,
													metadata: { ...(prev.metadata || {}), keywords: inputToKeywords(e.target.value) }
												}
											: prev
									)
								}
							/>
						</label>
					</div>
				<div className="text-xs font-mono text-slate-500">
					Resolved path: <span className="font-semibold">{resolvedPath}</span>
				</div>
			</>
		)}
		<div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
			<div className="flex items-center justify-between">
				<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Taxonomies</p>
				<button
					type="button"
					onClick={() => setTaxonomyEditing((prev) => !prev)}
					disabled={!editable}
					className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{taxonomyEditing ? "Done" : "Edit"}
				</button>
			</div>
			{taxonomyEditing ? (
				<TaxonomyMultiSelect
					options={taxonomies}
					value={taxonomyIds}
					onChange={(next) =>
						setDraft((prev) =>
							prev ? { ...prev, taxonomyIds: next } : prev
						)
					}
					placeholder="Assign taxonomies"
					helper="Filter and select taxonomies for this page."
					disabled={!editable}
				/>
			) : (
				<div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
					{taxonomyIds.length
						? taxonomyIds.map((id) => buildTaxonomyLabel(id, taxonomyMap)).join(", ")
						: "No taxonomies assigned."}
				</div>
			)}
		</div>
		</div>
	);
};

export default PageEditorMetadataPanel;
